import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import { intl } from 'lang/intl'

import { TtCollapsable, TtIcon } from '@23/react'
import { FormattedMessage } from 'react-intl'

import TtFormik from '../../../../global/formik-forms'
import { useHumanParticipants } from '../../../../webrtc/webRTCRoom'

import { FocusConfiguration } from '../FocusConfigurator'

import { FocusPanelModeParticipantOption } from './FocusPanelModeParticipantOption/FocusPanelModeParticipantOption'

import './FocusPanelMode.scss'

interface FocusPanelModeProps {
  side: 'left' | 'right'
  configuration: FocusConfiguration
}

const FocusPanelMode = ({ side, configuration }: FocusPanelModeProps) => {
  const { setFieldValue } = useFormikContext()

  const panelName = {
    left: intl.formatMessage({ defaultMessage: 'Left side panel' }),
    right: intl.formatMessage({ defaultMessage: 'Right side panel' }),
  }

  const participants = useHumanParticipants()

  const participantsWithCamera = participants
    .filter((p) => !!p.tracks.camera)
    .map((p) => p.id)

  const participantsWithScreen = participants
    .filter((p) => !!p.tracks.screen)
    .map((p) => p.id)

  useEffect(() => {
    if (configuration[`${side}PanelParticipantIds`] !== undefined) return
    if (configuration[`${side}PanelMode`] === 'participants')
      setFieldValue(`${side}PanelParticipantIds`, participantsWithCamera)
  }, [configuration, participants, participantsWithCamera, setFieldValue, side])

  useEffect(() => {
    if (configuration[`${side}PanelScreenIds`] !== undefined) return
    if (configuration[`${side}PanelMode`] === 'screens')
      setFieldValue(`${side}PanelScreenIds`, participantsWithScreen)
  }, [configuration, participants, participantsWithScreen, setFieldValue, side])

  return (
    <TtFormik.Fieldset
      className="focus-panel-mode"
      name={`name-${side}`}
      direction="vertical"
      hideLabel
      label={panelName[side]}
    >
      <TtFormik.Select<FocusConfiguration>
        className="focus-panel-mode-select"
        name={`${side}PanelMode`}
        label={panelName[side]}
      >
        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="empty"
        >
          <TtIcon slot="prefix" library="one/webinars" name="image" />
          <FormattedMessage defaultMessage="Disabled" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="tray-slides"
        >
          <TtIcon slot="prefix" library="one/webinars" name="image" />
          <FormattedMessage defaultMessage="WebinarTray Slides" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="tray-notes"
        >
          <TtIcon slot="prefix" library="one/webinars" name="note" />
          <FormattedMessage defaultMessage="WebinarTray Notes" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="stage"
        >
          <TtIcon slot="prefix" library="one" name="personal" />
          <FormattedMessage defaultMessage="Mix Output" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="participants"
        >
          <TtIcon slot="prefix" library="one" name="account-multiple" />
          <FormattedMessage defaultMessage="Speakers" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="screens"
        >
          <TtIcon slot="prefix" library="one" name="account-multiple" />
          <FormattedMessage defaultMessage="Screensharing" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="questions"
        >
          <TtIcon slot="prefix" library="one/webinars" name="question" />
          <FormattedMessage defaultMessage="Questions" />
        </TtFormik.Option>

        <TtFormik.Option<FocusConfiguration>
          name={`${side}PanelMode`}
          value="chat"
        >
          <TtIcon slot="prefix" library="one/webinars" name="comment" />
          <FormattedMessage defaultMessage="Chat" />
        </TtFormik.Option>
      </TtFormik.Select>

      <TtCollapsable
        open={['participants', 'screens'].includes(
          configuration[`${side}PanelMode`]
        )}
      >
        {configuration[`${side}PanelMode`] === 'participants' && (
          <TtFormik.Select<FocusConfiguration>
            name={`${side}PanelParticipantIds`}
            label={intl.formatMessage({ defaultMessage: 'Show speakers' })}
            multiple
          >
            {participantsWithCamera.map((pid) => (
              <FocusPanelModeParticipantOption
                key={pid}
                id={pid}
                side={side}
                type="Participant"
              />
            ))}
          </TtFormik.Select>
        )}
        {configuration[`${side}PanelMode`] === 'screens' && (
          <TtFormik.Select<FocusConfiguration>
            name={`${side}PanelScreenIds`}
            label={intl.formatMessage({ defaultMessage: 'Show screens' })}
            multiple
          >
            {participantsWithScreen.map((pid) => (
              <FocusPanelModeParticipantOption
                key={pid}
                id={pid}
                side={side}
                type="Screen"
              />
            ))}
          </TtFormik.Select>
        )}
      </TtCollapsable>
    </TtFormik.Fieldset>
  )
}

export { FocusPanelMode }
